import type { PricingTier } from '@/shared/hooks/usePricingTiers'

export interface PricingFeature {
  type: 'feature' | 'note' | 'separator' | 'inherited'
  text: string
  pricingNote?: string // Pour afficher les prix unitaires (ex: "+5€/dossier")
}

export interface PricingPlanFormatted {
  name: string
  description: string
  price: string
  period: string
  cta: string
  featured: boolean
  stripeLink: string
  features: PricingFeature[]
}

/**
 * Détecte automatiquement si un tier hérite d'un autre en comparant les feature_keys
 */
function detectInheritance(
  currentTier: PricingTier,
  allTiers: PricingTier[]
): { parentTier: PricingTier | null; ownFeatures: string[] } {
  // Trier les tiers par ordre croissant
  const sortedTiers = [...allTiers].sort((a, b) => a.tier_order - b.tier_order)

  // Trouver le tier parent potentiel (tier immédiatement avant dans l'ordre)
  const currentIndex = sortedTiers.findIndex((t) => t.tier_key === currentTier.tier_key)
  if (currentIndex <= 0) {
    // Premier tier, pas d'héritage
    return { parentTier: null, ownFeatures: currentTier.enabled_features }
  }

  const potentialParent = sortedTiers[currentIndex - 1]

  // Si pas de parent potentiel (pas de tier avant), pas d'héritage
  if (!potentialParent) {
    return { parentTier: null, ownFeatures: currentTier.enabled_features }
  }

  // Extraire les feature_keys des included_features
  const currentFeatureKeys =
    currentTier.included_features?.map((f) => f.key) || currentTier.enabled_features || []
  const parentFeatureKeys =
    potentialParent.included_features?.map((f) => f.key) || potentialParent.enabled_features || []

  // Vérifier si toutes les features du parent sont dans le tier courant
  const hasAllParentFeatures = parentFeatureKeys.every((key) => currentFeatureKeys.includes(key))

  if (hasAllParentFeatures && parentFeatureKeys.length > 0) {
    // C'est un héritage ! Filtrer les features propres (celles qui ne sont pas dans le parent)
    const ownFeatureKeys = currentFeatureKeys.filter((key) => !parentFeatureKeys.includes(key))
    return { parentTier: potentialParent, ownFeatures: ownFeatureKeys }
  }

  // Pas d'héritage détecté
  return { parentTier: null, ownFeatures: currentFeatureKeys }
}

/**
 * Transformer un PricingTier de la DB en format pour l'affichage
 * Détecte automatiquement l'héritage en comparant les feature_keys
 */
export function formatPricingTier(tier: PricingTier, allTiers: PricingTier[]): PricingPlanFormatted {
  const features: PricingFeature[] = []

  // 1. Ajouter les limites principales (résidences, lots, utilisateurs)
  if (tier.max_residences === -1) {
    features.push({ type: 'feature', text: 'Résidences illimitées' })
  } else {
    const residenceText =
      tier.max_residences === 1 ? `${tier.max_residences} résidence` : `${tier.max_residences} résidences`
    features.push({ type: 'feature', text: residenceText })
  }

  if (tier.max_lots === -1) {
    features.push({ type: 'feature', text: 'Lots illimités' })
  } else {
    features.push({ type: 'feature', text: `${tier.max_lots.toLocaleString()} lots` })
  }

  if (tier.max_users === -1) {
    features.push({ type: 'feature', text: 'Utilisateurs illimités' })
  } else {
    const userText = tier.max_users === 1 ? `${tier.max_users} utilisateur` : `${tier.max_users} utilisateurs`
    features.push({ type: 'feature', text: userText })
  }

  // 2. Détecter automatiquement l'héritage en comparant les feature_keys
  const { parentTier, ownFeatures } = detectInheritance(tier, allTiers)

  if (parentTier) {
    features.push({
      type: 'inherited',
      text: `Toutes les fonctionnalités ${parentTier.tier_name} +`,
    })
  }

  // 3. Ajouter seulement les features propres à ce tier (pas les héritées)
  if (tier.included_features && tier.included_features.length > 0) {
    // Filtrer pour ne garder que les features propres
    const ownFeatureDetails = tier.included_features.filter((f) => ownFeatures.includes(f.key))
    ownFeatureDetails.forEach((feature) => {
      features.push({ type: 'feature', text: feature.name })
    })
  }

  // 4. Note pour résidences supplémentaires (Essential uniquement)
  if (tier.allows_additional_residences && tier.additional_residence_price_ht) {
    features.push({ type: 'separator', text: '' })
    features.push({
      type: 'note',
      text: `+${tier.additional_residence_price_ht.toFixed(2)}€/mois par résidence supplémentaire (max ${tier.max_additional_residences})`,
    })
  }

  // 5. Ajouter le séparateur pour les features payantes à l'unité
  if (tier.unit_priced_features && tier.unit_priced_features.length > 0) {
    features.push({ type: 'separator', text: '' })

    // 6. Ajouter les features payantes à l'unité (communes à tous les tiers)
    tier.unit_priced_features.forEach((feature) => {
      features.push({
        type: 'feature',
        text: feature.name,
        pricingNote: feature.pricingNote || undefined,
      })
    })
  }

  // Formatter le prix depuis price_monthly_ht
  const formattedPrice = tier.price_monthly_ht.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return {
    name: tier.tier_name,
    description: tier.tier_description || '',
    price: `${formattedPrice}€`,
    period: 'HT / mois',
    cta: tier.cta_text,
    featured: tier.is_featured,
    stripeLink: tier.checkout_url,
    features,
  }
}

/**
 * Transformer tous les tiers de la DB
 */
export function formatAllPricingTiers(tiers: PricingTier[]): PricingPlanFormatted[] {
  return tiers.map((tier) => formatPricingTier(tier, tiers))
}
